import { Injectable } from '@angular/core';

import { DealerConsentOptions } from '../../customer/model/customer-information.model';
import { useI18n } from '../../i18n/services/i18n.service';
import { ConsentCheckbox, ConsentCheckboxGroup, ConsentSelection } from '../model/consent-checkbox';
import { NSCConsent } from '../model/nsc-consent';

@Injectable({
  providedIn: 'root',
})
export class ConsentMapper {
  private readonly i18n = useI18n();

  mapToDealerConsent(
    dealerConsentSelection: ConsentSelection | undefined,
    usageKeysToIgnore: string[] = [],
  ): ConsentCheckboxGroup[] | undefined {
    if (!dealerConsentSelection) {
      return;
    }
    return Object.entries(dealerConsentSelection)
      .filter(([usageKey]) => !usageKeysToIgnore.includes(usageKey))
      .map(
        ([usageKey]): ConsentCheckboxGroup => ({
          headline: undefined, // Currently dealer consents are not grouped, so a group headline is not required
          usageDefinitionId: undefined, // dealer consents do not have an usage definition id
          checkboxes: [{ key: usageKey, label: this.i18n.enum(usageKey.toUpperCase(), 'consent.dealerConsent') }],
        }),
      );
  }

  mapToDealerConsentOptions(consentSelection: ConsentSelection): DealerConsentOptions {
    return {
      email: consentSelection['email'] ?? false,
      inCar: consentSelection['inCar'] ?? false,
      letter: consentSelection['letter'] ?? false,
      phoneMessenger: consentSelection['phoneMessenger'] ?? false,
      customerProfile: consentSelection['customerProfile'] ?? false,
      dataProcessing: consentSelection['dataProcessing'] ?? false,
    };
  }

  /**
   * Is used to map NSC consent selection to dealer consent selection.
   */
  mapToDealerConsentSelection(consentSelection: ConsentSelection | undefined): ConsentSelection {
    if (!consentSelection) {
      return {
        email: false,
        inCar: false,
        letter: false,
        phoneMessenger: false,
        customerProfile: false,
        dataProcessing: false,
      };
    }

    const email = consentSelection['EMAIL'] ?? false;
    const inCar = consentSelection['IN_CAR'] ?? false;
    const letter = consentSelection['MAIL'] ?? false;
    const phoneMessenger = consentSelection['PHONE_MESSENGER_SERVICES'] ?? false;
    const anySelected = email || inCar || letter || phoneMessenger;

    return {
      email,
      inCar,
      letter,
      phoneMessenger,
      customerProfile: false,
      dataProcessing: anySelected,
    };
  }

  mapToConsentCheckboxGroups(nscConsents: NSCConsent[] | undefined): ConsentCheckboxGroup[] | undefined {
    return nscConsents?.map(
      (group): ConsentCheckboxGroup => ({
        headline: {
          text: group.textPrimary?.text ?? '',
          isHtml: group.textPrimary?.isHtml ?? false,
          popup: {
            text: group.textSecondary?.text ?? '',
            isHtml: group.textSecondary?.isHtml ?? false,
          },
        },
        usageDefinitionId: group.usageDefinitionId,
        checkboxes: group.usages.map(
          (usage): ConsentCheckbox => ({
            key: usage.name,
            label: usage.label,
          }),
        ),
      }),
    );
  }
}
